import type { StepState } from '@/components/ui/Stepper';
import type { Brand } from '@/features/brandHub/brand/types';

/** The wizard in order: the name creates the draft, every later step fills it. */
export const BRAND_CREATE_STEPS = [
  'name',
  'brand-guideline',
  'guideline-files',
  'look',
  'visual-elements',
  'audio',
  'done',
] as const;
export type BrandCreateStep = (typeof BRAND_CREATE_STEPS)[number];

/** Steps a brand does not need to become ready. */
export const OPTIONAL_STEPS: readonly BrandCreateStep[] = ['brand-guideline', 'visual-elements', 'audio'];

/** The steps that fill the brand, shown one at a time or all on one page. */
export const MATERIAL_STEPS: readonly BrandCreateStep[] = BRAND_CREATE_STEPS.filter(
  (step) => step !== 'name' && step !== 'done',
);

/** The three materials the look step gathers on one screen. */
export const LOOK_MATERIALS = ['logos', 'colours', 'font'] as const;

/** How much each step already holds, counted by the page. */
export type StepCounts = Partial<Record<BrandCreateStep, number>>;

export const BRAND_PARAM = 'brand';
export const STEP_PARAM = 'step';
export const VIEW_PARAM = 'view';

export const WIZARD_VIEWS = ['steps', 'page'] as const;
export type WizardView = (typeof WIZARD_VIEWS)[number];

/** The step the URL names. Without a brand there is only the name step. */
export function readBrandCreateStep(params: URLSearchParams): BrandCreateStep {
  if (!params.get(BRAND_PARAM)) return 'name';
  return BRAND_CREATE_STEPS.find((step) => step === params.get(STEP_PARAM)) ?? 'brand-guideline';
}

export function readWizardView(params: URLSearchParams): WizardView {
  return params.get(VIEW_PARAM) === 'page' ? 'page' : 'steps';
}

export function stepIndex(step: BrandCreateStep): number {
  return BRAND_CREATE_STEPS.indexOf(step);
}

export function nextStep(step: BrandCreateStep): BrandCreateStep {
  return BRAND_CREATE_STEPS[Math.min(BRAND_CREATE_STEPS.length - 1, stepIndex(step) + 1)];
}

export function previousStep(step: BrandCreateStep): BrandCreateStep {
  return BRAND_CREATE_STEPS[Math.max(0, stepIndex(step) - 1)];
}

/** How many of the look step's three materials the brand carries. Each counts once its list holds one. */
export function lookMaterialsDone(brand: Brand, logoCount: number): number {
  const held: Record<(typeof LOOK_MATERIALS)[number], boolean> = {
    logos: logoCount > 0,
    colours: brand.colours.length > 0,
    font: brand.fonts.length > 0,
  };
  return LOOK_MATERIALS.filter((material) => held[material]).length;
}

/**
 * Derived from the counts the page streams, so the stepper never disagrees with the lists.
 * A step that holds anything is complete; one the user moved past while it holds nothing is skipped.
 */
export function stepState(
  step: BrandCreateStep,
  brand: Brand | null,
  counts: StepCounts,
  reachedIndex = 0,
): StepState {
  if (step === 'name') return brand ? 'complete' : 'pending';
  if (!brand) return 'locked';
  if (step === 'done') return 'pending';
  if ((counts[step] ?? 0) > 0) return 'complete';
  return stepIndex(step) < reachedIndex ? 'skipped' : 'pending';
}

/** A label carrying how much a step already holds, or the label alone when it holds nothing. */
export function withCount(label: string, count: number | undefined, total?: number): string {
  if (!count) return label;
  return total ? `${label} (${count}/${total})` : `${label} (${count})`;
}
